import { PropertyActionReturns } from './actions';
import * as Types from './types';
import * as FormTypes from '../forms/types';
import { v4 as uuidv4 } from 'uuid';
import rfdc from 'rfdc';
import { LoanType } from '@apply/constants';
import { IAddress } from '../cross';

export const isRentalUnitEmpty = (unit: Types.RentalUnit) => {
    if (!unit) return true;

    return !unit.bathrooms && !unit.bedrooms && !unit.garageBays && !unit.rent && !unit.squareFeet && unit.occupied === undefined;
};

export const areAttachmentValueEqual = (left: Types.Attachment, right: Types.Attachment) => {
    if (!left && !right) {
        return true;
    }

    if (!left && right) {
        return false;
    }

    if (left && !right) {
        return false;
    }

    return (
        left.handleMode === right.handleMode &&
        left.uploadedFilename === right.uploadedFilename &&
        left.uploadedFileApiId === right.uploadedFileApiId &&
        left.formId === right.formId &&
        left.formStorage === right.formStorage &&
        left.data === right.data
    );
};

export const requiredRentalUnitsForStructureType = (type: Types.RentalStructureType): number => {
    if (!type) {
        return 0;
    }

    switch (type) {
        case 'Detached House':
            return 1;
        case 'Townhouse':
            return 1;
        case 'Duplex':
            return 2;
        case 'Triplex':
            return 3;
        case 'Quadplex':
            return 4;
        case '5+ Unit':
            return 5;
        case 'Single Condo Unit Within Larger Structure':
            return 1;
        default:
            throw new Error('unhandled rental unit count scenario - unsupported structure type');
    }
};

export const PropertiesReducerHelpers = {
    isRentalUnitEmpty,
    requiredRentalUnitsForStructureType,
    areAttachmentValueEqual,
};

// justification for throwing errors in default cases - would only happen if developer
// "misused" this function or new structure types/actions were added and this function
// wasn't appropriately updated
export const requiredRentalUnits = (action: PropertyActionReturns): number => {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { type: actionType, payload } = action as any;
    switch (actionType) {
        case Types.UPDATE_STRUCTURE_TYPE: {
            const newType = payload.value as Types.RentalStructureType;
            return requiredRentalUnitsForStructureType(newType);
        }
        case Types.UPDATE_NUMBER_RENTAL_UNITS: {
            return payload.value as number;
        }
        default: {
            throw new Error('unhandled rental unit count scenario - unsupported action type');
        }
    }
};

export const constructRentalUnits = (requiredCount: number): Types.RentalUnit[] => {
    if (requiredCount <= 0) {
        return [];
    }

    return [...Array.from(Array(requiredCount).keys())].map(() => {
        return { ...Types.rentalUnitInitialState, guid: uuidv4() };
    });
};

export const singlePropertyReducer = (state: Types.Property, action: PropertyActionReturns) => {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { type: actionType, payload } = action as any;
    switch (actionType) {
        case Types.UPDATE_STRUCTURE_TYPE: {
            if (payload.loanType === LoanType.Rental || payload.loanType === LoanType.BuildRent) {
                const newUnitsRequired = requiredRentalUnits(action) - state.rentalUnits.length;
                if (newUnitsRequired > 0) {
                    const rentalUnits = [...state.rentalUnits, ...constructRentalUnits(newUnitsRequired)];
                    return {
                        ...state,
                        structureType: payload.value,
                        rentalUnits,
                    };
                } else if (newUnitsRequired < 0) {
                    const newEndIndex = state.rentalUnits.length + newUnitsRequired - 1; // zero based counter
                    const rentalUnits = state.rentalUnits.filter((_, index) => index <= newEndIndex);
                    return {
                        ...state,
                        structureType: payload.value,
                        rentalUnits,
                    };
                } else {
                    return {
                        ...state,
                        structureType: payload.value,
                    };
                }
            } else {
                return {
                    ...state,
                    structureType: payload.value,
                };
            }
        }
        case Types.UPDATE_LAND_SIZE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                landSize: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_ADDRESS_LINE1: {
            const address: IAddress = {
                ...state.address,
                street1: payload.value,
            };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_ADDRESS_LINE2: {
            const address: IAddress = {
                ...state.address,
                street2: payload.value,
            };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_ADDRESS_CITY: {
            const address: IAddress = { ...state.address, city: payload.value };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_ADDRESS_STATE: {
            const address: IAddress = {
                ...state.address,
                state: payload.value,
            };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_ADDRESS_COUNTY: {
            const address: IAddress = {
                ...state.address,
                county: payload.value,
            };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_ADDRESS_ZIPCODE: {
            const address: IAddress = {
                ...state.address,
                zipCode: payload.value,
            };
            return {
                ...state,
                address,
            };
        }
        case Types.UPDATE_LEGAL_DESCRIPTION: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                legalDescription: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_BUDGET: {
            if (areAttachmentValueEqual(state.nonRental?.budget, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                budget: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_LAND_BUDGET: {
            if (areAttachmentValueEqual(state.nonRental?.landBudget, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                landBudget: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_FIXFLIP_BUDGET: {
            if (areAttachmentValueEqual(state.nonRental?.fixFlipBudget, payload.value)) {
                return state;
            }

            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                fixFlipBudget: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_PLANS: {
            if (areAttachmentValueEqual(state.nonRental?.plans, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                plans: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_SPEC_SHEET: {
            if (areAttachmentValueEqual(state.nonRental?.specSheet, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                specSheet: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_SURVEY: {
            if (areAttachmentValueEqual(state.nonRental?.survey, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                survey: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_PRESOLD: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                preSold: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NON_RENTAL_NAME: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                name: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NON_RENTAL_EMAIL: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                email: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NON_RENTAL_PHONE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                phone: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_CONTACT_IS_PRIMARY: {
            if (payload.value.contactIsPrimary === true) {
                const nonRental: Types.NonRental = {
                    ...Types.nonRentalInitialState,
                    ...state.nonRental,
                    ...payload.value,
                };
                return {
                    ...state,
                    nonRental,
                };
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                contactIsPrimary: payload.value.contactIsPrimary,
                email: '',
                name: '',
                phone: '',
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_PRESALE_CONTRACT: {
            if (areAttachmentValueEqual(state.nonRental?.presaleContract, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                presaleContract: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_CONSTRUCTION_STARTED: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                constructionStarted: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_IS_ACQUISITION_FINANCING: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                isAquisitionFinancing: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_ANY_EXISTING_DEBT: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                anyExistingDebt: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NUMBER_CONSTRUCTION_UNITS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                numberUnits: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NUMBER_UNITS_INCOME_RESTRICTED: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                unitsIncomeRestricted: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_UNIT_NUMBER: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                unitNumber: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_PURCHASED_LESS_THAN_TWO_YEAR_AGO: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                purchasedLessThan2YearsAgo: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_MORE_THAN_TENK_EXPENDITURES: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                moreThan10kExpenditures: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_CURRENT_DEBT: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                currentDebt: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_NONRENTAL_CURRENT_DEBT: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                currentDebt: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NUMBER_RENTAL_UNITS: {
            let rentalUnits = state.rentalUnits;
            const newUnitsRequired = requiredRentalUnits(action) - state.rentalUnits.length;
            if (newUnitsRequired > 0) {
                rentalUnits = [...state.rentalUnits, ...constructRentalUnits(newUnitsRequired)];
            } else if (newUnitsRequired < 0 && state.rentalUnits.some((unt) => isRentalUnitEmpty(unt))) {
                const trimmedUnits = state.rentalUnits.filter((unit) => !isRentalUnitEmpty(unit));
                rentalUnits = [...trimmedUnits, ...constructRentalUnits(requiredRentalUnits(action) - trimmedUnits.length)];
            }
            // zero requires no action
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                numberRentalUnits: payload.value,
            };
            return {
                ...state,
                rental,
                rentalUnits,
            };
        }
        case Types.UPDATE_PURCHASE_CONTRACT: {
            if (areAttachmentValueEqual(state.rental?.purchaseContract, payload.value)) {
                return state;
            }
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                purchaseContract: { ...payload.value },
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_CAPITAL_EXPENDITURES: {
            if (areAttachmentValueEqual(state.rental?.capitalExpenditures, payload.value)) {
                return state;
            }
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                capitalExpenditures: { ...payload.value },
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_UNIT: {
            const rentalUnits = state.rentalUnits.map((unit) => {
                if (unit.guid !== payload.value.guid) {
                    return unit;
                }
                const results = {
                    ...Types.rentalUnitInitialState,
                    ...unit,
                    ...payload.value,
                };
                return results;
            });
            return {
                ...state,
                rentalUnits,
            };
        }
        case Types.UPDATE_PROVIDE_RENTAL_UNITS_LATER: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                provideRentalUnitsLater: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_GENERAL_CONTRATOR_RESUME: {
            if (areAttachmentValueEqual(state.nonRental?.generalContractorResume, payload.value)) {
                return state;
            }
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                generalContractorResume: { ...payload.value },
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_GENERAL_CONTRATOR_WILL_WORK: {
            const primary = payload.value
                ? {
                      contactIsPrimary: undefined,
                      email: '',
                      name: '',
                      phone: '',
                  }
                : {};
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                generalContractorWillWork: payload.value,
                ...primary,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_GENERAL_CONTRATOR_NAME: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                generalContractorName: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_UPDATE_YEAR_BUILT: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                yearBuilt: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_UPDATE_BUILD_TYPE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                buildType: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_RENTAL_BUILD_TYPE: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                buildType: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_BUILDRENT_TYPE: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                buildRentTypeId: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_UNDERGROUND_STORAGE_TANKS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                undergroundStorageTanks: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_ACQUISITIONS_DATE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                acquisitionDate: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_IS_FORECLOSURE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                isForeclosure: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_NEED_FUNDING_AT_PURCHASE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                needFundingAtPurchase: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_INTENDED_WORK: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                intendedWork: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_EXPECTED_LISTING_PRICE: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                expectedListingPrice: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_TARGET_LOAN_REQUEST: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                targetLoanRequest: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_RENTAL_TARGET_LOAN_REQUEST: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                targetLoanRequest: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_NUMBER_UNITS_INCOME_RESTRICTED: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                unitsIncomeRestricted: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_PREFERRED_TERM_REQUEST: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                preferredTermType: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_YEAR_BUILT: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                yearBuilt: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_IS_ACQUISITION_FINANCING: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                isAquisitionFinancing: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_RENTAL_IS_LOAN_PROCEEDS_USED_PROPERTY_IMPROVEMENTS: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                isLoanProceedsUsedPropertyImprovements: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_PURCHASE_DATE: {
            const rental: Types.Rental = {
                ...Types.rentalInitialState,
                ...state.rental,
                purchaseDate: payload.value,
            };
            return {
                ...state,
                rental,
            };
        }
        case Types.UPDATE_CURRENT_PLATTED_LOTS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                currentPlattedLots: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_PLANNED_PLATTED_LOTS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                plannedPlattedLots: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_REQUIRED_ZONING_OR_ENTITLEMENT: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                requiredZoningOrEntitlement: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_DESCRIBE_REQUIRED_INPSECTIONS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                describeRequiredInspections: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_WILL_BUILD_ON_LAND_YOURSELF: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                willBuildOnLandYourself: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_EXIT_PLANS: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                exitPlans: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        case Types.UPDATE_STARTED_SITE_WORK: {
            const nonRental: Types.NonRental = {
                ...Types.nonRentalInitialState,
                ...state.nonRental,
                startedSiteWork: payload.value,
            };
            return {
                ...state,
                nonRental,
            };
        }
        /* istanbul ignore next */
        default: {
            return state;
        }
    }
};

export const propertiesReducer = (
    state: Types.PropertiesState = Types.propertiesInitialState,
    action: PropertyActionReturns
): Types.PropertiesState => {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { type: actionType, payload } = action as any;
    switch (actionType) {
        case Types.RESET: {
            return {
                ...Types.propertiesInitialState,
            };
        }
        case Types.ADD_PROPERTY: {
            const newProperty = {
                ...Types.propertyInitialState,
                guid: uuidv4(),
            };
            return {
                ...state,
                items: [...state.items, newProperty],
                currentPropertyId: newProperty.guid,
                isDirty: true,
            };
        }
        case Types.REMOVE_PROPERTY: {
            if (payload.guid) {
                // if we're removing the current one, set current to the first remaining item
                const currentPropertyId =
                    state.currentPropertyId === payload.guid
                        ? state.items.find((itm) => itm.guid !== payload.guid)?.guid
                        : state.currentPropertyId;
                return {
                    ...state,
                    items: state.items.filter((item) => payload.guid !== item.guid),
                    currentPropertyId,
                    isDirty: true,
                };
            } else {
                return state;
            }
        }
        case Types.REPLACE_PROPERTIES_STATE: {
            const items = [];
            payload.properties.items.forEach((itm: Types.Property) => {
                items.push({ ...itm });
            });
            const currentPropertyId = payload.currentPropertyId || items?.[items.length - 1]?.guid;
            return {
                ...state,
                items,
                isDirty: false,
                currentPropertyId,
                forms: [...payload.properties.forms],
            };
        }
        case Types.UPDATE_IS_DIRTY: {
            return { ...state, isDirty: payload.isDirty };
        }
        case Types.UPDATE_CURRENT_PROPERTY_ID: {
            return {
                ...state,
                currentPropertyId: payload.currentPropertyId,
                isDirty: false,
            };
        }
        case Types.SAVE_FORM: {
            const newform: FormTypes.FormState = rfdc()(payload.form);
            const forms = [...state.forms.filter((f) => f.guid !== newform.guid), newform];
            return {
                ...state,
                forms,
            };
        }
        case Types.UPDATE_ADDRESS_LINE1:
        case Types.UPDATE_ADDRESS_LINE2:
        case Types.UPDATE_ADDRESS_CITY:
        case Types.UPDATE_ADDRESS_STATE:
        case Types.UPDATE_ADDRESS_COUNTY:
        case Types.UPDATE_ADDRESS_ZIPCODE:
        case Types.UPDATE_LEGAL_DESCRIPTION:
        case Types.UPDATE_BUDGET:
        case Types.UPDATE_LAND_BUDGET:
        case Types.UPDATE_FIXFLIP_BUDGET:
        case Types.UPDATE_PLANS:
        case Types.UPDATE_SPEC_SHEET:
        case Types.UPDATE_SURVEY:
        case Types.UPDATE_PRESOLD:
        case Types.UPDATE_CONTACT_IS_PRIMARY:
        case Types.UPDATE_NON_RENTAL_NAME:
        case Types.UPDATE_NON_RENTAL_EMAIL:
        case Types.UPDATE_NON_RENTAL_PHONE:
        case Types.UPDATE_PRESALE_CONTRACT:
        case Types.UPDATE_CONSTRUCTION_STARTED:
        case Types.UPDATE_IS_ACQUISITION_FINANCING:
        case Types.UPDATE_ANY_EXISTING_DEBT:
        case Types.UPDATE_LAND_SIZE:
        case Types.UPDATE_STRUCTURE_TYPE:
        case Types.UPDATE_UNIT_NUMBER:
        case Types.UPDATE_PURCHASED_LESS_THAN_TWO_YEAR_AGO:
        case Types.UPDATE_MORE_THAN_TENK_EXPENDITURES:
        case Types.UPDATE_CURRENT_DEBT:
        case Types.UPDATE_NONRENTAL_CURRENT_DEBT:
        case Types.UPDATE_NUMBER_RENTAL_UNITS:
        case Types.UPDATE_NUMBER_CONSTRUCTION_UNITS:
        case Types.UPDATE_NUMBER_UNITS_INCOME_RESTRICTED:
        case Types.UPDATE_PURCHASE_CONTRACT:
        case Types.UPDATE_CAPITAL_EXPENDITURES:
        case Types.UPDATE_RENTAL_UNIT:
        case Types.UPDATE_PROVIDE_RENTAL_UNITS_LATER:
        case Types.UPDATE_GENERAL_CONTRATOR_RESUME:
        case Types.UPDATE_GENERAL_CONTRATOR_NAME:
        case Types.UPDATE_GENERAL_CONTRATOR_WILL_WORK:
        case Types.UPDATE_UPDATE_YEAR_BUILT:
        case Types.UPDATE_UPDATE_BUILD_TYPE:
        case Types.UPDATE_RENTAL_BUILD_TYPE:
        case Types.UPDATE_RENTAL_BUILDRENT_TYPE:
        case Types.UPDATE_UNDERGROUND_STORAGE_TANKS:
        case Types.UPDATE_ACQUISITIONS_DATE:
        case Types.UPDATE_IS_FORECLOSURE:
        case Types.UPDATE_NEED_FUNDING_AT_PURCHASE:
        case Types.UPDATE_INTENDED_WORK:
        case Types.UPDATE_EXPECTED_LISTING_PRICE:
        case Types.UPDATE_RENTAL_YEAR_BUILT:
        case Types.UPDATE_RENTAL_IS_ACQUISITION_FINANCING:
        case Types.UPDATE_RENTAL_IS_LOAN_PROCEEDS_USED_PROPERTY_IMPROVEMENTS:
        case Types.UPDATE_PURCHASE_DATE:
        case Types.UPDATE_TARGET_LOAN_REQUEST:
        case Types.UPDATE_RENTAL_TARGET_LOAN_REQUEST:
        case Types.UPDATE_RENTAL_NUMBER_UNITS_INCOME_RESTRICTED:
        case Types.UPDATE_PREFERRED_TERM_REQUEST:
        case Types.UPDATE_CURRENT_PLATTED_LOTS:
        case Types.UPDATE_PLANNED_PLATTED_LOTS:
        case Types.UPDATE_REQUIRED_ZONING_OR_ENTITLEMENT:
        case Types.UPDATE_DESCRIBE_REQUIRED_INPSECTIONS:
        case Types.UPDATE_WILL_BUILD_ON_LAND_YOURSELF:
        case Types.UPDATE_EXIT_PLANS:
        case Types.UPDATE_STARTED_SITE_WORK: {
            const items = state.items.map((itm) => {
                if (itm.guid !== payload.guid) {
                    return itm;
                }
                return singlePropertyReducer(itm, action);
            });
            return {
                ...state,
                items,
                isDirty: true,
            };
        }
        default: {
            return state;
        }
    }
};
